import { useDispatch, useSelector } from 'react-redux';
import { ZodError } from 'zod';
import { toast } from 'sonner';
import { ChangeEvent, useState } from 'react';
import { FileImage, Loader2, Upload, X, Youtube } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { DropZone } from '@/components/ui/drop-zone';
import ErrorMessage from '@/components/ErrorMessage';
import {
  allowedMediaExtensions,
  mediaUploadSchema,
} from '@/redux/post/post.schema';
import {
  setProjectEditorData,
  selectProjectEditorData,
  removeFileFromUploadQueue,
  addFileToUploadQueue,
} from '@/redux/post/post.slice';
import {
  AttachmentType,
  IAttachmentCategory,
  AttachmentParentType,
} from '@/redux/attachment/attachment.d';
import { useDeleteAttachmentMutation } from '@/redux/attachment/attachment.api.slice';
import hashFile from '@/lib/xxhash';

function MediaUpload({ errors }: { errors: { [key: string]: string } }) {
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const dispatch = useDispatch();
  const projectEditorData = useSelector(selectProjectEditorData);
  const [isDeleting, setIsDeleting] = useState('');
  const [deleteAttachment] = useDeleteAttachmentMutation();

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const modelFiles = event.target.files;
    if (!modelFiles || modelFiles.length === 0) return;

    const files = Array.from(modelFiles);

    // Hash in parallel, but queue in the order the user picked the files —
    // dispatching inside the Promise.all callbacks queues them in
    // hash-completion order instead (big files finish last).
    const fileHashes = await Promise.all(files.map((file) => hashFile(file)));

    files.forEach((file, index) => {
      dispatch(
        addFileToUploadQueue({
          parentId: projectEditorData._id,
          parentType: AttachmentParentType.POST,
          category: IAttachmentCategory.postMedia,
          resourceType: AttachmentType.image,
          fileName: file.name,
          fileHash: fileHashes[index].toString(),
          fileSize: file.size,
          preview: URL.createObjectURL(file),
        })
      );
    });
  };

  const handleRemoveFile = async (fileHash: string) => {
    setIsDeleting(fileHash);
    try {
      dispatch(
        removeFileFromUploadQueue({
          fileHash,
          category: IAttachmentCategory.postMedia,
        })
      );
      const existingMedia = projectEditorData.mediaFiles.find(
        (media) =>
          media.fileHash === fileHash &&
          media.category === IAttachmentCategory.postMedia
      );
      if (!existingMedia) return;
      await deleteAttachment({
        attachmentId: existingMedia._id,
      });
      dispatch(
        setProjectEditorData({
          ...projectEditorData,
          mediaFiles: projectEditorData.mediaFiles.filter(
            (media) => media.fileHash !== fileHash
          ),
        })
      );
    } catch {
      toast.error('Failed to delete file');
    } finally {
      setIsDeleting('');
    }
  };

  const handleAddYoutubeUrl = () => {
    if (youtubeUrl.trim()) {
      try {
        mediaUploadSchema.parse({
          mediaFiles: projectEditorData.mediaFiles,
          fileUploadQueue: projectEditorData.fileUploadQueue,
          youtubeAttachments: [
            ...(projectEditorData.youtubeAttachments || []),
            youtubeUrl.trim(),
          ],
        });

        dispatch(
          setProjectEditorData({
            ...projectEditorData,
            youtubeAttachments: [
              ...(projectEditorData.youtubeAttachments || []),
              youtubeUrl.trim(),
            ],
          })
        );

        setErrorMessage('');
        setYoutubeUrl('');
      } catch (error) {
        if (error instanceof ZodError) {
          setErrorMessage(error.errors[0].message);
        }
      }
    }
  };

  const handleRemoveYoutubeUrl = (urlToRemove: string) => {
    dispatch(
      setProjectEditorData({
        ...projectEditorData,
        youtubeAttachments: (projectEditorData.youtubeAttachments || []).filter(
          (url) => url !== urlToRemove
        ),
      })
    );
  };

  const uploadQueueMedia = projectEditorData.fileUploadQueue.filter(
    (file) => file.category === IAttachmentCategory.postMedia
  );

  // Filter out YouTube attachments from mediaFiles for separate display
  const existingMediaFiles = projectEditorData.mediaFiles.filter(
    (file) => file.resourceType !== AttachmentType.youtube
  );

  // Get saved YouTube attachments from mediaFiles
  const savedYoutubeAttachments = projectEditorData.mediaFiles.filter(
    (file) => file.resourceType === AttachmentType.youtube
  );

  // Filter out newly added URLs that are already saved
  const savedYoutubeUrls = new Set(
    savedYoutubeAttachments.map((attachment) => attachment.resourcePath)
  );
  const newYoutubeAttachments = projectEditorData.youtubeAttachments.filter(
    (url) => !savedYoutubeUrls.has(url)
  );

  const existingAndUploadQueueMedia = [
    ...existingMediaFiles,
    ...uploadQueueMedia,
  ];

  const handleRemoveSavedYoutubeAttachment = async (attachmentId: string) => {
    setIsDeleting(attachmentId);
    try {
      const attachmentToDelete = projectEditorData.mediaFiles.find(
        (media) => media._id === attachmentId
      );
      await deleteAttachment({ attachmentId });
      dispatch(
        setProjectEditorData({
          ...projectEditorData,
          mediaFiles: projectEditorData.mediaFiles.filter(
            (media) => media._id !== attachmentId
          ),
          youtubeAttachments: projectEditorData.youtubeAttachments.filter(
            (url) => url !== attachmentToDelete?.resourcePath
          ),
        })
      );
    } catch {
      toast.error('Failed to delete attachment');
    } finally {
      setIsDeleting('');
    }
  };

  return (
    <div className="h-full flex flex-col font-tertiary">
      <Card className="h-full w-full max-w-6xl mx-auto flex flex-col">
        <CardContent className="p-6 overflow-y-auto flex-1">
          <div className="flex flex-col gap-6">
            <div className="space-y-3">
              <h3 className="text-sm font-medium">Upload Media Files</h3>
              <DropZone onDrop={handleFileChange}>
                <Upload className="mx-auto h-12 w-12 text-muted-foreground" />
                <div className="mt-4">
                  <p className="text-sm text-muted-foreground">
                    Select a file or drag and drop here
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    CAD Design Files (Vectric, Carveco, LightBurn, ETC.)
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Images, Vectors, Documents (.SVG .STL .DXF .LBN .LBN2 .LBRN
                    .LBRN2)
                  </p>
                </div>
                <Input
                  type="file"
                  className="hidden"
                  onChange={handleFileChange}
                  accept={allowedMediaExtensions
                    .map((ext) => `.${ext}`)
                    .join(',')}
                  id="file-upload"
                  multiple
                />
                <Button
                  variant="outline"
                  className="mt-4 border-gray-400 border-2"
                  onClick={() =>
                    document.getElementById('file-upload')?.click()
                  }
                >
                  Select file
                </Button>
              </DropZone>
              <ErrorMessage error={errors.mediaFiles} />
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-medium">
                Add YouTube Links (Optional)
              </h3>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  type="url"
                  placeholder="Enter YouTube URL"
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                  className="flex-1 h-11"
                />
                <Button
                  variant="outline"
                  onClick={handleAddYoutubeUrl}
                  className="border-gray-400 border-2 px-6 w-full sm:w-auto"
                >
                  Add YouTube URL
                </Button>
              </div>
              <ErrorMessage error={errors.youtubeAttachments || errorMessage} />
            </div>

            {(savedYoutubeAttachments.length > 0 ||
              newYoutubeAttachments.length > 0 ||
              existingAndUploadQueueMedia.length > 0) && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium">Uploaded Files</h3>
                <div className="flex flex-col gap-2 max-h-64 overflow-auto">
                  <div className="space-y-2 max-w-4xl">
                    {/* Display saved YouTube attachments from mediaFiles */}
                    {savedYoutubeAttachments.length > 0 &&
                      savedYoutubeAttachments.map((attachment) => (
                        <div
                          className="flex items-center gap-2 py-2 px-3 border rounded-lg hover:bg-muted/50 transition-colors"
                          key={attachment._id}
                        >
                          <Youtube className="text-[#FF0000] h-4 w-4 flex-shrink-0" />
                          <div
                            className="min-w-0 flex-1 break-all text-sm line-clamp-2"
                            title={attachment.resourcePath}
                          >
                            {attachment.fileName || attachment.resourcePath}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 rounded-full cursor-pointer p-0"
                            onClick={() =>
                              handleRemoveSavedYoutubeAttachment(attachment._id)
                            }
                          >
                            {isDeleting === attachment._id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <X className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      ))}
                    {/* Display newly added YouTube URLs (not yet saved) */}
                    {newYoutubeAttachments.length > 0 &&
                      newYoutubeAttachments.map((url) => (
                        <div
                          className="flex items-center gap-2 py-2 px-3 border rounded-lg hover:bg-muted/50 transition-colors"
                          key={url}
                        >
                          <Youtube className="text-[#FF0000] h-4 w-4 flex-shrink-0" />
                          <div
                            className="min-w-0 flex-1 break-all text-sm line-clamp-2"
                            title={url}
                          >
                            {url}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 rounded-full cursor-pointer p-0"
                            onClick={() => handleRemoveYoutubeUrl(url)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    {existingAndUploadQueueMedia &&
                      existingAndUploadQueueMedia.length > 0 &&
                      existingAndUploadQueueMedia.map((file) => {
                        return (
                          <div
                            className="flex items-center gap-2 py-2 px-3 border rounded-lg hover:bg-muted/50 transition-colors"
                            key={`${file.fileName}-${file.fileSize}`}
                          >
                            <FileImage className="text-[#0F91D2] h-4 w-4 flex-shrink-0" />
                            <div className="min-w-0 flex-1 break-all text-sm font-medium line-clamp-2">
                              {file.fileName}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 rounded-full cursor-pointer p-0"
                              onClick={() => handleRemoveFile(file.fileHash)}
                            >
                              {isDeleting === file.fileHash ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <X className="h-3 w-3" />
                              )}
                            </Button>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default MediaUpload;
